"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { FaCookieBite, FaCheck, FaChevronDown, FaLock, FaChartLine, FaBullhorn } from "react-icons/fa";

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

type CookiePrefs = {
  analytics: boolean;
  marketing: boolean;
};

const DEFAULT_PREFS: CookiePrefs = {
  analytics: true,
  marketing: true,
};

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(DEFAULT_PREFS);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const applyConsent = (nextPrefs: CookiePrefs) => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      // gtag.js only processes `arguments` objects from the dataLayer; pushing a
      // plain array is silently ignored, so the consent update would never apply.
      function gtag(..._args: any[]) {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      }

      const analyticsState = nextPrefs.analytics ? "granted" : "denied";
      const marketingState = nextPrefs.marketing ? "granted" : "denied";

      gtag("consent", "update", {
        ad_storage: marketingState,
        ad_user_data: marketingState,
        ad_personalization: marketingState,
        analytics_storage: analyticsState,
      });


      const allAccepted = nextPrefs.analytics && nextPrefs.marketing;
      localStorage.setItem("cookie_consent", allAccepted ? "accepted" : "custom");
      localStorage.setItem("cookie_consent_prefs", JSON.stringify(nextPrefs));
    }

    setShowBanner(false);
  };

  const acceptAll = () => applyConsent(DEFAULT_PREFS);
  const savePreferences = () => applyConsent(prefs);

  const togglePref = (key: keyof CookiePrefs) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  const categories = [
    {
      key: null,
      icon: <FaLock className="w-3.5 h-3.5" />,
      iconClass: "bg-slate-100 text-slate-500",
      title: "Strictly Necessary",
      description: "Essential for the website to function properly.",
      enabled: true,
      locked: true,
    },
    {
      key: "analytics" as const,
      icon: <FaChartLine className="w-3.5 h-3.5" />,
      iconClass: "bg-indigo-50 text-indigo-600",
      title: "Performance & Analytics",
      description: "Helps us understand how visitors interact with the site (Google Analytics).",
      enabled: prefs.analytics,
      locked: false,
    },
    {
      key: "marketing" as const,
      icon: <FaBullhorn className="w-3.5 h-3.5" />,
      iconClass: "bg-violet-50 text-violet-600",
      title: "Marketing",
      description: "Used to deliver relevant advertisements and track ad performance.",
      enabled: prefs.marketing,
      locked: false,
    },
  ];

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie Consent Banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 max-w-md z-[99999] p-5 bg-white/95 backdrop-blur-md border border-slate-200 rounded-2xl shadow-2xl transition-all duration-300 ease-out"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700 rounded-xl shrink-0 mt-0.5 shadow-xs">
            <FaCookieBite className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">
              We value your privacy 🍪
            </h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              We use cookies to enhance your experience, analyze site traffic, and support our
              marketing efforts. By clicking &quot;Accept All&quot;, you consent to our use of all
              cookies. Read our{" "}
              <Link
                href="/privacy-policy"
                className="text-indigo-600 hover:underline font-medium"
              >
                Privacy Policy
              </Link>.
            </p>
          </div>
        </div>

        {showDetails && (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {categories.map((cat) => (
              <div
                key={cat.title}
                className="flex items-start justify-between gap-3 p-3 bg-slate-50/80 border border-slate-100 rounded-xl"
              >
                <div className="flex items-start gap-2.5">
                  <div className={`p-1.5 rounded-lg shrink-0 mt-0.5 ${cat.iconClass}`}>
                    {cat.icon}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800">{cat.title}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                      {cat.description}
                    </p>
                  </div>
                </div>
                {cat.locked ? (
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-full mt-1">
                    Always On
                  </span>
                ) : (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={cat.enabled}
                    aria-label={`Toggle ${cat.title} cookies`}
                    onClick={() => cat.key && togglePref(cat.key)}
                    className={`relative shrink-0 w-10 mt-1 rounded-full transition-colors duration-200 cursor-pointer ${
                      cat.enabled ? "bg-gradient-to-r from-indigo-500 to-violet-500" : "bg-slate-300"
                    }`}
                    style={{ height: "22px" }}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform duration-200 ${
                        cat.enabled ? "translate-x-[18px]" : "translate-x-0"
                      }`}
                    />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
          {showDetails ? (
            <button
              type="button"
              onClick={savePreferences}
              className="flex-1 px-4 py-2.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-center cursor-pointer"
            >
              Save Preferences
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="flex-1 px-4 py-2.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              More Details
              <FaChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
          <button
            type="button"
            onClick={acceptAll}
            className="flex-1 px-4 py-2.5 text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 rounded-xl shadow-md shadow-indigo-500/20 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <FaCheck className="w-3 h-3" />
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
